// The only place the v1 screens change the game (PLAN-03 §3). Every player
// action goes through the store's `dispatch`, which runs the sim's own rule and
// — in Veteran — records it in the action log (PLAN-04 S1), so subscribers,
// autosave, the renderer and the server replay all see what the player tapped.
//
// Later streams add the chore, bird, warning and setup actions here.

import type { GameStateStore } from '../core/store';
import { createChick, createInitialState } from '../core/initialState';
import type { Breed, ChoreId, GameState, HousingType } from '../core/types';
import { Rng } from '../core/rng';
import { t } from '../i18n';
import { HANDLE_STRESS_RELIEF } from '../sim/actions';
import type { FixtureChoice } from '../sim/spots';
import { calendarDayKey } from '../sim/time';
import { inspectFindings, type InspectFinding } from '../sim/inspect';

/** One chick the player bought on screen 02. */
export interface ChickPurchase {
  breed: Breed;
  count: number;
}

export interface StartNewGameInput {
  housing: HousingType;
  purchases: ChickPurchase[];
  /** Straight-run is cheaper and may turn out a rooster; sexed is ~90% hens. */
  sexed: boolean;
  veteran: boolean;
  /** One name per chick, in purchase order. */
  names: string[];
}

/**
 * Builds the new save from the existing initial-state builder and applies what
 * the onboarding screens chose. The starter flock is discarded: the player's
 * own chicks are the flock, which is the first-minute promise in REFOCUS §5.
 */
export function startNewGame(store: GameStateStore, input: StartNewGameInput): GameState {
  const seed = (Date.now() >>> 0) || 1;
  const state = createInitialState(seed, input.housing);
  const rng = new Rng(seed);

  const flock = [];
  let nameIndex = 0;
  for (const purchase of input.purchases) {
    for (let i = 0; i < purchase.count; i++) {
      const name = (input.names[nameIndex] ?? '').trim();
      nameIndex++;
      // Straight-run means the sex is a surprise; the game decides it later at
      // 6–8 weeks (REFOCUS §5), so every chick starts as a hen and the reveal
      // is a real event rather than a hidden roll at purchase time.
      const chick = createChick(
        rng,
        purchase.breed,
        name || t('onboard.defaultChickName', { n: String(nameIndex) }),
        { x: 2 + (nameIndex % 3), y: 3 },
        14,
        'hen',
      );
      flock.push(chick);
    }
  }

  state.flock = flock;
  // Pecking order starts with the first chicks bought; the sim re-sorts as the
  // flock settles over the first 7–14 days (REFOCUS §8).
  flock.forEach((chick, index) => {
    chick.hierarchyRank = index;
  });
  // REFOCUS §3: Veteran is chosen on the breed screen, so it is set here and
  // nowhere else. CLEANUP C5 renames this field; that is a one-line change.
  state.veteran.enabled = input.veteran;
  state.veteran.unlocked = input.veteran;
  if (input.veteran) {
    // Season start: the league day counts from here and the log starts empty.
    state.veteran.startedAtDay = Math.floor(state.simHour / 24);
    state.veteran.actions = [];
  }
  // The app reads this to decide whether onboarding still has to run: it means
  // this save has been through the first-run screens.
  state.progression.onboarded = true;

  // `replace` is the store's wholesale swap; it emits STATE_LOADED itself, so
  // the renderer and every subscriber resync off one event.
  store.replace(state);
  // Written at once rather than left to the 10s autosave tick: this is the only
  // moment where closing the tab loses work the player cannot redo, and the
  // onboarding screens are exactly where someone closes the tab.
  void store.save();
  return state;
}

// --- chores -----------------------------------------------------------------

export interface ChoreResult {
  performed: boolean;
  /** i18n key of the message to show when nothing happened. */
  messageKey: string | null;
  messageParams?: Record<string, string | number>;
  /** The fix lives in placing equipment, not in the store. */
  needsEquipment: boolean;
  reward: number;
}

/**
 * The "Quick" path through a chore: one tap, done.
 *
 * The rules are the sim's own (`sim/actions.ts`), reached through the store's
 * `dispatch` so a Veteran chore lands in the action log (PLAN-04 S1). The
 * calendar day rides along as an argument: the server replays the log later
 * and must count the chore toward the day it was done, not the day it replays.
 */
export function completeChore(store: GameStateStore, chore: ChoreId): ChoreResult {
  const result = store.dispatch({ type: 'chore', args: { chore, dayKey: calendarDayKey() } });
  return {
    performed: result.performed,
    messageKey: result.messageKey,
    messageParams: result.messageParams,
    needsEquipment: result.needsEquipment ?? false,
    reward: result.reward ?? 0,
  };
}

// --- catching an escapee (REFOCUS §12) -------------------------------------

export interface CatchResult {
  /** True when the attempt actually ran. False for a bird already in the run. */
  performed: boolean;
  /** True when the bird is back in the run. */
  caught: boolean;
  messageKey: string | null;
  messageParams?: Record<string, string | number>;
}

/**
 * One attempt at getting a bird back over the fence. The odds are the escape
 * sim's own (`sim/escape.ts`), and the attempt is a logged action like any
 * other, so a replay of the log catches the same bird.
 */
export function catchEscapee(store: GameStateStore, chickenId: string): CatchResult {
  const result = store.dispatch({ type: 'catch', args: { chickenId } });
  return {
    performed: result.performed,
    caught: result.caught ?? false,
    messageKey: result.messageKey,
    messageParams: result.messageParams,
  };
}

// --- feed (U4) -------------------------------------------------------------

export interface BuyAndPourInput {
  type: 'starter' | 'grower' | 'layer';
  bag: 'small' | 'big';
}

export interface BuyAndPourResult {
  performed: boolean;
  messageKey: string | null;
  messageParams?: Record<string, string | number>;
  /** The ration that actually went into the feeder. */
  poured: 'starter' | 'grower' | 'layer' | null;
  /** Eggits the day paid out, when this pour completed the whole checklist. */
  reward: number;
}

/**
 * Buy a bag and pour it, in one tap (`sim/actions.ts` owns the rule). A wrong
 * ration is allowed: REFOCUS §12a is explicit that the consequence is real,
 * and the screen says which one before the tap.
 *
 * The calendar day rides along as an argument, exactly as it does for
 * `completeChore`: the sim credits the chore to the day it was poured, and the
 * server replay has to agree with the phone rather than read its own clock.
 */
export function buyAndPourFeed(store: GameStateStore, input: BuyAndPourInput): BuyAndPourResult {
  const result = store.dispatch({
    type: 'buyFeedBag',
    args: { feedType: input.type, size: input.bag, dayKey: calendarDayKey() },
  });
  return {
    performed: result.performed,
    messageKey: result.messageKey,
    messageParams: result.messageParams,
    poured: result.poured ?? null,
    reward: result.reward ?? 0,
  };
}

// --- the bird screen (U5) --------------------------------------------------

export interface InspectResult {
  findings: InspectFinding[];
}

/**
 * Inspect: the eight close-up cards, read off the bird's own conditions and
 * meters (REFOCUS §12c). Each card is `sim/inspect.ts` naming what the sim
 * found on that spot, so a card cannot report a healthy spot the sim disagrees
 * with — the placeholder this replaced reported a half-full crop at a good tone
 * no matter what, and had no card at all for a bird with crop trouble.
 */
export function inspectBird(store: GameStateStore, chickenId: string): InspectResult {
  const bird = store.getState().flock.find((candidate) => candidate.id === chickenId);
  if (!bird) return { findings: [] };
  // An inspection is how a hidden condition becomes acknowledged, so the sim
  // gets told it has been looked at (same rule the old inspector used).
  store.dispatch({ type: 'inspect', args: { chickenId } });
  return { findings: inspectFindings(bird) };
}

/**
 * Handle: daily handling makes a bird tamer, and tame birds stress less
 * (BHV-15). PLAN-04 owns the real tameness stat; until then this is a real,
 * small, honest effect on stress and nothing else.
 */
export function handleBird(store: GameStateStore, chickenId: string): number {
  store.dispatch({ type: 'handle', args: { chickenId } });
  return HANDLE_STRESS_RELIEF;
}

/** Treat: the sim's own treatment logic (sim/disease.ts), one condition at a time. */
export function treatBird(store: GameStateStore, chickenId: string): boolean {
  const state = store.getState();
  const bird = state.flock.find((candidate) => candidate.id === chickenId);
  const condition = bird?.conditions[0];
  if (!bird || !condition) return false;
  return store.dispatch({ type: 'treat', args: { chickenId, condition: condition.id } }).performed;
}

// --- setup: spots and fixtures (PLAN-04 S2) --------------------------------

/**
 * Puts an owned item into a spot, or moves it there if it already stands
 * somewhere. Moving is free, but the birds need about a day to find it again
 * (REFOCUS §7). Returns why not when the spot is locked, taken or the wrong kind.
 */
export function placeItem(
  store: GameStateStore,
  spotId: string,
  structureId: string,
): { ok: boolean; reason?: string } {
  const result = store.dispatch({ type: 'placeItem', args: { spotId, structureId } });
  return { ok: result.performed, reason: result.reason };
}

export function moveItem(
  store: GameStateStore,
  from: string,
  to: string,
): { ok: boolean; reason?: string } {
  const result = store.dispatch({ type: 'moveItem', args: { from, to } });
  return { ok: result.performed, reason: result.reason };
}

/** Roof, ventilation or roost bar, paid at the season's price. */
export function setFixture(store: GameStateStore, choice: FixtureChoice): { ok: boolean; reason?: string } {
  const result = store.dispatch({ type: 'setFixture', args: choice });
  return { ok: result.performed, reason: result.reason };
}

/**
 * The brooder lamp dial, 0–100 (REFOCUS §5). This is the first-minute step in
 * which the player owns a piece of equipment rather than being handed one, so it
 * goes through `dispatch` like every other tap: in Veteran it is in the log the
 * server replays.
 */
export function setBrooderLamp(store: GameStateStore, setting: number): { ok: boolean; reason?: string } {
  const result = store.dispatch({ type: 'setBrooderLamp', args: { setting } });
  return { ok: result.performed, reason: result.reason };
}
